
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Phone } from 'lucide-react';

const navLinks = [
  { name: 'About Us', to: '/about-us' },
  { name: 'Brochures and Publications', to: '/brochures' },
  { name: 'Corporate Governance & Tech', to: '/corporate-governance-tech' },
  { name: 'Arbitration and ADR', to: '/arbitration-adr' },
  { name: 'Our People', to: '/team-profile' },
  { 
    name: 'AI Legal Issues', 
    to: '/sectors/machine-learning',
    children: [
      { name: 'Machine Learning Compliance', to: '/sectors/machine-learning' },
      { name: 'Agentic AI Regulations', to: '/sectors/agentic-ai' },
      { name: 'Generative AI Governance', to: '/sectors/generative-ai' },
      { name: 'NLP Privacy Law', to: '/sectors/nlp-privacy' },
      { name: 'Robotics Legal Framework', to: '/sectors/robotics-legal' },
    ],
  },
  { name: 'Mentorship', to: '/mentorship' },
];

const socialIcons = [
  { 
    name: 'X', 
    href: 'https://x.com/LawandcodeAfro', 
    icon: (
      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"/>
      </svg>
    ) 
  },
  { 
    name: 'LinkedIn', 
    href: 'https://www.linkedin.com/in/lawandcodeafrica', 
    icon: (
      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ) 
  },
  { 
    name: 'Phone', 
    href: 'tel:+254731797354', 
    icon: <Phone size={20} />
  },
  { 
    name: 'WhatsApp', 
    href: 'https://wa.me/254731797354', 
    icon: (
      <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.488"/>
      </svg>
    ) 
  }
];

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const isActiveLink = (to: string, children?: { to: string }[]) => {
    if (location.pathname === to) return true;
    if (children) {
      return children.some(child => location.pathname === child.to);
    }
    return false;
  };

  const handleNavClick = (to: string) => {
    if (to !== '#') {
      navigate(to);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleDropdownClick = () => {
    setOpenDropdown(prev => prev ? null : 'toggle');
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      {/* Top info bar */}
      <div className="bg-gray-100 py-2 text-sm">
        <div className="w-full px-4 flex justify-between items-center">
          <div className="flex gap-6 text-gray-600">
            <span>Email: <a href="https://mail.google.com/mail/?view=cm&fs=1&to=lawandcodeafrica@gmail.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors underline-offset-2 hover:underline">lawandcodeafrica@gmail.com</a></span>
            <span className="italic text-primary">Future worth pursuing</span>
          </div>
          <div className="flex gap-3 items-center">
            {socialIcons.map(s => (
              <a 
                href={s.href} 
                target="_blank" 
                rel="noopener noreferrer" 
                key={s.name} 
                className="text-gray-600 hover:text-primary transition-colors"
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="py-4">
        <div className="w-full px-4 flex items-center justify-between">
          <Link
            to="/"
            onClick={() => setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)}
            className="flex flex-col cursor-pointer mr-4"
          >
            <span className="text-xl font-bold tracking-tight text-primary leading-none">
              Law <span className="font-serif italic font-normal text-secondary text-2xl align-middle">&amp;</span> Code <span className="text-gray-700">Africa</span>
            </span>
            <span className="text-[10px] text-gray-500 tracking-[0.25em] uppercase mt-1">Law and Tech Commentary</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex gap-1 items-center">
            {navLinks.map(link => link.children ? (
              <div 
                key={link.name} 
                className="relative"
                onMouseEnter={() => setOpenDropdown(link.name)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button 
                  className={`px-2 py-2 text-sm font-medium transition-colors flex items-center whitespace-nowrap ${
                    isActiveLink(link.to, link.children) ? 'text-primary' : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {link.name}
                  <svg className="ml-1 w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                <div 
                  className={`absolute left-0 mt-0 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20 transition-opacity duration-200 ${
                    openDropdown === link.name ? 'opacity-100 visible' : 'opacity-0 invisible'
                  }`}
                >
                  {link.children.map(child => (
                    <button 
                      onClick={() => handleNavClick(child.to)}
                      key={child.name} 
                      className={`block w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                        location.pathname === child.to ? 'text-primary' : 'text-gray-700 hover:text-primary'
                      }`}
                    >
                      {child.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button 
                onClick={() => handleNavClick(link.to)}
                key={link.name} 
                className={`px-2 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  isActiveLink(link.to) ? 'text-primary' : 'text-gray-700 hover:text-primary'
                }`}
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map(link => (
              <div key={link.name}>
                <button 
                  onClick={() => handleNavClick(link.to)}
                  className={`block w-full text-left py-2 font-medium ${
                    isActiveLink(link.to, link.children) ? 'text-primary' : 'text-gray-700 hover:text-primary'
                  }`}
                >
                  {link.name}
                </button>
                {link.children && (
                  <div className="ml-4 space-y-1">
                    {link.children.map(child => (
                      <button 
                        onClick={() => handleNavClick(child.to)}
                        key={child.name} 
                        className={`block w-full text-left py-1 ${
                          location.pathname === child.to ? 'text-primary' : 'text-gray-600 hover:text-primary'
                        }`}
                      >
                        {child.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
